import { useState, useRef, useEffect, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigationType } from "react-router-dom";

// Global scroll positions storage (persists across renders)
const scrollPositionsMap = new Map();

// Scroll Restoration Component
const ScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevLocationRef = useRef(null);

  // Save scroll position before navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      scrollPositionsMap.set(location.pathname, window.scrollY);
    };

    // Save on scroll (throttled)
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        scrollPositionsMap.set(location.pathname, window.scrollY);
      }, 100);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      // Save position when component unmounts (navigating away)
      scrollPositionsMap.set(location.pathname, window.scrollY);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [location.pathname]);

  // Handle scroll on navigation
  useLayoutEffect(() => {
    if (navigationType === 'POP') {
      // Going back/forward - restore scroll position
      const savedPosition = scrollPositionsMap.get(location.pathname);
      if (savedPosition !== undefined && savedPosition > 0) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo(0, savedPosition);
        }, 0);
      }
    } else {
      // Going forward (PUSH/REPLACE) - scroll to top instantly
      window.scrollTo(0, 0);
    }

    prevLocationRef.current = location.pathname;
  }, [location.pathname, navigationType]);

  return null;
};
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Phone, Mail, MapPin, CheckCircle, Home as HomeIcon, Grid3X3, DoorOpen, MessageSquare, Menu, X, Award, ExternalLink, ChevronLeft, ChevronRight, Clock, Users, Wrench, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Contact Form Component with mailto
const ContactForm = ({ compact = false }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    messaggio: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Richiesta Preventivo - Spaziocasa+');
    const body = encodeURIComponent(
      `Nome: ${formData.nome}\n` +
      `Email: ${formData.email}\n` +
      `Telefono: ${formData.telefono}\n\n` +
      `Messaggio:\n${formData.messaggio}`
    );
    window.location.href = `mailto:info@spaziocasapiu.it?subject=${subject}&body=${body}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Input 
          placeholder="Nome e Cognome" 
          className="border-gray-300" 
          value={formData.nome}
          onChange={(e) => setFormData({...formData, nome: e.target.value})}
          required
        />
      </div>
      <div>
        <Input 
          type="email" 
          placeholder="Email" 
          className="border-gray-300" 
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          required
        />
      </div>
      <div>
        <Input 
          type="tel" 
          placeholder="Telefono" 
          className="border-gray-300" 
          value={formData.telefono}
          onChange={(e) => setFormData({...formData, telefono: e.target.value})}
        />
      </div>
      <div>
        <Textarea 
          placeholder="Messaggio" 
          rows={compact ? 3 : 4} 
          className="border-gray-300" 
          value={formData.messaggio}
          onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
          required
        />
      </div>
      <Button type="submit" className={`w-full bg-amber-500 hover:bg-amber-600 text-white ${compact ? '' : 'py-6'}`}>
        Invia Richiesta
      </Button>
    </form>
  );
};

// Company Info
const COMPANY = {
  name: "spaziocasapiu.it",
  displayName: "Spazio casa+",
  companyName: "EDIL FA.TA. snc",
  piva: "03226500282",
  email: "info@spaziocasapiu.it",
  phone: "0499790560",
  address: "Via San Polo 87/B",
  city: "35020 Sant'Angelo di Piove di Sacco - Padova",
  logo: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/adrwtl6j_ChatGPT%20Image%2026%20gen%202026%2C%2011_44_47.png",
  logoNavbar: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/zovgv3gp_logo2%20oro.png",
  logoFooter: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/adrwtl6j_ChatGPT%20Image%2026%20gen%202026%2C%2011_44_47.png"
};

// Brands data
const brandsData = [
  { name: "Marazzi", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/7bi6lhza_marazzi.png", url: "https://www.marazzi.it" },
  { name: "Energieker", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/4xh4548i_logo%20energieker.jpg", url: "https://www.energieker.it/it/" },
  { name: "Tuscania", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/jko9derp_tuscania-logo.webp", url: "https://tuscaniagres.it/" },
  { name: "Unicom Starker", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/oliiwt3y_logo-unicom.webp", url: "https://www.unicomstarker.com/" },
  { name: "Mariner", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/49nsm47o_logo-mariner-big.png", url: "https://www.cermariner.it/it/" },
  { name: "Old Sax", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/4g97ymbu_logo%20old-sax.jpg-1.jpeg", url: "https://www.oldsaxceramic.com/" },
  { name: "Virag", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/3cghkoc5_logo%20virag.png", url: "https://www.virag.com/" },
  { name: "Castfloor", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/h0cr242w_logo%20castfloor-laminati.jpg", url: "https://castfloor.com/" },
  { name: "Veka", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/8vsqwez8_logo_veka.png", url: null },
  { name: "Salamander", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/q1xs2q4g_logo%20salamander.webp", url: null },
];

// Hero Slides
const heroSlides = [
  {
    image: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/63rifbe0_pavimenti%20e%20rivestimenti%20HOME.png",
    title: "Soluzioni per la tua casa",
    subtitle: "Da oltre 20 anni trasformiamo gli spazi con pavimenti, rivestimenti e serramenti di qualità superiore."
  },
  {
    image: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/26lsak0f_infissi.jpeg",
    title: "Serramenti di qualità",
    subtitle: "Finestre e porte che garantiscono isolamento termico e acustico per il massimo comfort."
  }
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/chi-siamo", label: "Chi Siamo" },
    { path: "/pavimenti", label: "Pavimenti e Rivestimenti" },
    { path: "/serramenti", label: "Serramenti" },
    { path: "/marchi", label: "I Nostri Marchi" },
    { path: "/contatti", label: "Contatti" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-navy ${scrolled ? 'shadow-lg' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center">
            <img src={COMPANY.logoNavbar} alt="Spazio Casa Più" className="h-24 md:h-64" style={{ marginTop: '23px' }} />
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors hover:text-amber-400 ${
                  location.pathname === item.path ? "text-amber-400" : "text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden bg-navy py-4 border-t border-white/10">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block py-3 text-white hover:text-amber-400 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section Component (Static)
const HeroSection = () => {
  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Image - Fully Visible */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/63rifbe0_pavimenti%20e%20rivestimenti%20HOME.png)` }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center text-white px-4 max-w-4xl">
          {/* Blue semi-transparent band around text and button */}
          <div style={{ backgroundColor: 'rgba(30, 58, 95, 0.75)' }} className="py-10 px-12 rounded-lg">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fadeInUp">
              Soluzioni per la tua casa
            </h1>
            <p className="text-lg md:text-xl mb-8 animate-fadeInUp animation-delay-200">
              Ogni casa è unica. Contattaci per una consulenza personalizzata.
            </p>
            <div className="flex justify-center animate-fadeInUp animation-delay-400">
              <Link to="/contatti">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg">
                  Richiedi Preventivo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 text-white animate-bounce hidden md:block">
        <ChevronRight className="w-8 h-8 rotate-90" />
      </div>
    </section>
  );
};

// Products Grid Component
const ProductsGrid = () => {
  const products = [
    { 
      name: "Pavimenti", 
      image: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/33vvz1rn_pavimenti.jpg",
      link: "/pavimenti",
      size: "large"
    },
    { 
      name: "Rivestimenti", 
      image: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ao58mf44_UNIVERSAL-AMBIENTE-MIRABELLmet-Nichel.jpg",
      link: "/pavimenti",
      size: "medium"
    },
    { 
      name: "Serramenti", 
      image: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/26lsak0f_infissi.jpeg",
      link: "/serramenti",
      size: "medium"
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">I Nostri Prodotti</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Scopri la nostra vasta gamma di soluzioni per la tua casa
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <Link 
              key={index} 
              to={product.link}
              className={`group relative overflow-hidden rounded-lg ${
                product.size === 'large' ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              <div className={`relative ${product.size === 'large' ? 'h-96 md:h-full' : 'h-64'}`}>
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div style={{ backgroundColor: 'rgba(30, 58, 95, 0.75)', width: '150px' }} className="rounded-lg p-4">
                    <h3 className="text-xl font-bold text-white mb-1">{product.name}</h3>
                    <span className="inline-flex items-center text-amber-400 font-medium group-hover:translate-x-2 transition-transform">
                      Scopri <ArrowRight className="w-5 h-5 ml-2" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section Component
const AboutSection = () => (
  <section className="py-20 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">
            Showroom
          </h2>
          <p className="text-gray-600 text-lg mb-6 leading-relaxed">
            Spaziocasa+ nasce da una storia di famiglia, di lavoro concreto e di passione per il costruire.
          </p>
          <p className="text-gray-600 text-lg mb-6 leading-relaxed">
            Pavimenti in gres e SPC, rivestimenti e serramenti per vivere meglio ogni ambiente.
          </p>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Dalla progettazione alla posa, ti accompagniamo in ogni fase del tuo progetto.
          </p>
          <Link to="/contatti">
            <Button className="bg-navy hover:bg-navy/90 text-white px-8 py-6 text-lg">
              Vieni a trovarci <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/tknr0t35_sla%20mostra%202.jpg"
            alt="Showroom"
            className="rounded-lg shadow-2xl"
          />
          <div className="absolute -bottom-6 -left-6 bg-amber-500 text-white p-6 rounded-lg shadow-xl">
            <p className="text-4xl font-bold">20+</p>
            <p className="text-sm">Anni di esperienza</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section Component
const ServicesSection = () => {
  const services = [
    { icon: Users, title: "Consulenza Gratuita", desc: "I nostri esperti ti guidano nella scelta migliore" },
    { icon: Star, title: "Qualità Garantita", desc: "Solo prodotti certificati dei migliori marchi" },
    { icon: Wrench, title: "Installazione Professionale", desc: "Posa in opera eseguita da personale qualificato" },
    { icon: Clock, title: "Assistenza Post-Vendita", desc: "Supporto continuo anche dopo l'acquisto" },
  ];

  return (
    <section className="py-20 bg-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">I Nostri Servizi</h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            Un servizio completo per realizzare la casa dei tuoi sogni
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div key={index} className="text-center group">
              <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <service.icon className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
              <p className="text-white/70">{service.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Brands Carousel Component
const BrandsCarousel = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-navy text-center mb-10">I Marchi che Trattiamo</h2>
        <div className="relative">
          <button 
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-navy shadow-lg rounded-full p-3 hover:bg-navy/80 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          
          <div 
            ref={scrollRef}
            className="flex gap-12 items-center overflow-x-auto scrollbar-hide px-16 py-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex animate-scroll gap-12 items-center">
              {[...brandsData, ...brandsData].map((brand, index) => {
                const logoHeight = brand.name === 'Veka' ? '50px' : '60px';
                return brand.url ? (
                  <a 
                    key={index} 
                    href={brand.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                  >
                    <img 
                      src={brand.logo} 
                      alt={brand.name} 
                      className="w-auto object-contain"
                      style={{ height: logoHeight }}
                    />
                  </a>
                ) : (
                  <div key={index} className="flex-shrink-0 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300">
                    <img 
                      src={brand.logo} 
                      alt={brand.name} 
                      className="w-auto object-contain"
                      style={{ height: logoHeight }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          
          <button 
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-navy shadow-lg rounded-full p-3 hover:bg-navy/80 transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>
        
        <div className="text-center mt-8">
          <Link to="/marchi">
            <Button variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white">
              Vedi tutti i marchi <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Contact CTA Section
const ContactCTA = () => (
  <section className="py-20 bg-gradient-to-r from-navy to-navy/80">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Google Maps - Centered and Enlarged */}
      <div className="rounded-lg overflow-hidden shadow-2xl mb-12" style={{ height: '500px' }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d89456.48770686825!2d11.794543!3d45.3726855!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477eda3b8a4a3bff%3A0x63a5d0f3e7e8a8f0!2sVia%20San%20Polo%2C%2087%2FB%2C%2035020%20Sant&#39;Angelo%20di%20Piove%20di%20Sacco%20PD!5e0!3m2!1sit!2sit!4v1705900000000!5m2!1sit!2sit"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Spazio Casa+ Location"
        />
      </div>
      
      {/* Contact Info - Centered */}
      <div className="bg-white rounded-lg p-8 shadow-2xl max-w-2xl mx-auto">
        <h3 className="text-2xl font-bold text-navy mb-6 text-center">Informazioni di Contatto</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-navy">Indirizzo</h4>
              <p className="text-gray-600 text-sm">{COMPANY.address}</p>
              <p className="text-gray-600 text-sm">{COMPANY.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-navy">Telefono</h4>
              <a href={`tel:${COMPANY.phone}`} className="text-amber-600 hover:underline">{COMPANY.phone}</a>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-navy">Email</h4>
              <a href={`mailto:${COMPANY.email}`} className="text-amber-600 hover:underline">{COMPANY.email}</a>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-navy">Azienda</h4>
              <p className="text-gray-600 text-sm">{COMPANY.companyName}</p>
              <p className="text-gray-600 text-sm">P.IVA: {COMPANY.piva}</p>
            </div>
          </div>
        </div>
        <div className="mt-8 text-center">
          <Link to="/contatti">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white">
              Contattaci <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="bg-navy text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid md:grid-cols-3 gap-8">
        {/* Logo & Description */}
        <div>
          <img src={COMPANY.logoFooter} alt={COMPANY.displayName} className="h-40 mb-4 brightness-0 invert" />
          <p className="text-white/70 text-sm" style={{ marginLeft: '19px' }}>
            <strong>EDIL FA.TA. snc</strong> - P.IVA: {COMPANY.piva}
          </p>
        </div>
        
        {/* Quick Links */}
        <div style={{ marginLeft: '190px' }}>
          <h4 className="font-semibold text-lg mb-4">Link Rapidi</h4>
          <ul className="space-y-1">
            <li><Link to="/" className="text-white/70 hover:text-amber-400 transition-colors text-sm">Home</Link></li>
            <li><Link to="/chi-siamo" className="text-white/70 hover:text-amber-400 transition-colors text-sm">Chi Siamo</Link></li>
            <li><Link to="/pavimenti" className="text-white/70 hover:text-amber-400 transition-colors text-sm">Pavimenti e Rivestimenti</Link></li>
            <li><Link to="/serramenti" className="text-white/70 hover:text-amber-400 transition-colors text-sm">Serramenti</Link></li>
            <li><Link to="/marchi" className="text-white/70 hover:text-amber-400 transition-colors text-sm">I Nostri Marchi</Link></li>
            <li><Link to="/contatti" className="text-white/70 hover:text-amber-400 transition-colors text-sm">Contatti</Link></li>
          </ul>
        </div>
        
        {/* Contact Info */}
        <div>
          <h4 className="font-semibold text-lg mb-4">Contatti</h4>
          <div className="space-y-1">
            <a 
              href="https://www.google.com/maps/search/?api=1&query=Via+San+Polo+87%2FB+35020+Sant'Angelo+di+Piove+di+Sacco+Padova"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 text-white/70 hover:text-amber-400 transition-colors text-sm"
            >
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{COMPANY.address}, {COMPANY.city}</span>
            </a>
            <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 text-white/70 hover:text-amber-400 transition-colors text-sm">
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-white/70 hover:text-amber-400 transition-colors text-sm">
              <Mail className="w-4 h-4" />
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>
    </div>
    
    {/* Copyright */}
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-center text-white/50 text-sm">
          © {new Date().getFullYear()} {COMPANY.displayName}. Tutti i diritti riservati.
        </p>
      </div>
    </div>
  </footer>
);

// Home Page
const HomePage = () => (
  <div>
    <HeroSection />
    <ProductsGrid />
    <AboutSection />
    <ServicesSection />
    <BrandsCarousel />
    <ContactCTA />
  </div>
);

// Pavimenti Page
const PavimentiPage = () => {
  const products = [
    { name: "Pavimenti", slug: "pavimenti-gres", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/33vvz1rn_pavimenti.jpg" },
    { name: "Rivestimenti Ceramici", slug: "rivestimenti-ceramici", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ao58mf44_UNIVERSAL-AMBIENTE-MIRABELLmet-Nichel.jpg" },
    { name: "SPC (Stone Plastic Composite)", slug: "spc", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/6py3rtvy_spc%20niagara.jpg" },
    { name: "Arredo Bagno", slug: "arredo-bagno", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/vfhmt9qa_menu.png" },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-80 bg-navy">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" 
          style={{ backgroundImage: `url(${products[0].img})` }} 
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Pavimenti e Rivestimenti</h1>
            <p className="text-xl text-white/80">Scopri le nostre soluzioni per ogni ambiente</p>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => (
              <Link key={index} to={`/pavimenti/${product.slug}`}>
                <Card className="overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-all duration-300">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={product.img} 
                      alt={product.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                    />
                  </div>
                  <CardContent className="p-6 bg-gray-50">
                    <h3 className="text-lg font-semibold text-navy text-center">{product.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Arredo Bagno Page
const ArredoBagnoPage = () => {
  const products = [
    { name: "Sanitari", slug: "sanitari", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/gy9z0vow_same.jpg" },
    { name: "Piatti Doccia", slug: "piatti-doccia", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/atmu0apf_piatto.jpg" },
    { name: "Box Doccia", slug: "box-doccia", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/rwgrl5yz_doccia.jpg" },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-64 bg-navy">
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="text-3xl md:text-4xl font-bold text-white">Arredo Bagno</h1>
            <p className="text-xl text-white/80 mt-2">Soluzioni complete per il tuo bagno</p>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Link key={index} to={`/pavimenti/arredo-bagno/${product.slug}`}>
                <Card className="overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-all duration-300">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={product.img} 
                      alt={product.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                    />
                  </div>
                  <CardContent className="p-6 bg-gray-50">
                    <h3 className="text-lg font-semibold text-navy text-center">{product.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          {/* Back Link */}
          <div className="mt-12">
            <Link to="/pavimenti" className="inline-flex items-center text-amber-500 hover:text-amber-600 font-medium">
              <ChevronLeft className="w-5 h-5 mr-1" /> Pavimenti e Rivestimenti
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Sanitari Page
const SanitariPage = () => (
  <ProductDetailPage 
    title="Sanitari"
    description="Scopri la nostra selezione di sanitari di alta qualità per il tuo bagno. Design moderno e funzionalità per ogni esigenza."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/gy9z0vow_same.jpg"
    features={["Design moderno e linee pulite", "Materiali di alta qualità", "Facile pulizia e manutenzione", "Ampia scelta di modelli"]}
    backLink="/pavimenti/arredo-bagno"
    backLabel="Arredo Bagno"
  />
);

// Piatti Doccia Page
const PiattiDocciaPage = () => (
  <ProductDetailPage 
    title="Piatti Doccia"
    description="Piatti doccia in diverse dimensioni e materiali. Soluzioni su misura per ogni spazio bagno."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/atmu0apf_piatto.jpg"
    features={["Diversi materiali: ceramica, resina, pietra", "Dimensioni personalizzabili", "Antiscivolo e sicuri", "Design minimal ed elegante"]}
    backLink="/pavimenti/arredo-bagno"
    backLabel="Arredo Bagno"
  />
);

// Box Doccia Page
const BoxDocciaPage = () => (
  <ProductDetailPage 
    title="Box Doccia"
    description="Box doccia in vetro temperato con profili minimal. Soluzioni a battente, scorrevoli e walk-in."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/rwgrl5yz_doccia.jpg"
    features={["Vetro temperato di sicurezza", "Profili in alluminio o senza profili", "Apertura a battente o scorrevole", "Trattamento anticalcare"]}
    backLink="/pavimenti/arredo-bagno"
    backLabel="Arredo Bagno"
  />
);

// Serramenti Page
const SerramentiPage = () => {
  const products = [
    { name: "Finestre in PVC", slug: "finestre-pvc", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/40xulk7x_finestra%20bianca%20bella.jpg" },
    { name: "Finestre in Alluminio", slug: "finestre-alluminio", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ulrw8nnp_aliplast%20alluminio.png" },
    { name: "Avvolgibili", slug: "avvolgibili", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ge2xxjl1_avvolgibili.webp" },
    { name: "Portoni Sezionali", slug: "portoni-sezionali", img: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ze63o48e_sezionale.webp" },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-80 bg-navy">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" 
          style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/26lsak0f_infissi.jpeg)` }} 
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Serramenti</h1>
            <p className="text-xl text-white/80">Qualità e isolamento per il tuo comfort</p>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => (
              <Link key={index} to={`/serramenti/${product.slug}`}>
                <Card className="overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-all duration-300">
                  <div className="aspect-square overflow-hidden bg-white p-4">
                    <img 
                      src={product.img} 
                      alt={product.name} 
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" 
                    />
                  </div>
                  <CardContent className="p-6 bg-gray-50">
                    <h3 className="text-lg font-semibold text-navy text-center">{product.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-navy text-center mb-10">Vantaggi dei Nostri Serramenti</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {["Risparmio energetico fino al 40%", "Isolamento acustico certificato", "Resistenza agli agenti atmosferici", "Manutenzione minima", "Garanzia fino a 10 anni"].map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Product Detail Page Component
const ProductDetailPage = ({ title, description, img, features, backLink, backLabel }) => (
  <div className="pt-20">
    {/* Hero */}
    <section className="relative h-64 bg-navy">
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-3xl md:text-4xl font-bold text-white">{title}</h1>
        </div>
      </div>
    </section>

    {/* Content */}
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img src={img} alt={title} className="w-full h-auto object-contain" />
          </div>
          <div>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">{description}</p>
            <h3 className="text-xl font-semibold text-navy mb-4">Caratteristiche</h3>
            <ul className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
            <Link to="/contatti">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white">
                Richiedi un Preventivo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Back Link */}
        <div className="mt-12">
          <Link to={backLink} className="inline-flex items-center text-amber-500 hover:text-amber-600 font-medium">
            <ChevronLeft className="w-5 h-5 mr-1" /> {backLabel}
          </Link>
        </div>
      </div>
    </section>
  </div>
);

// Product Detail Pages
const GresPage = () => {
  const categories = [
    { name: "Effetto Pietra", slug: "effetto-pietra", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/pg489duz_tuscania-colosseoplus-6.jpg" },
    { name: "Effetto Cemento", slug: "effetto-cemento", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/biohjakb_cemento%20grigio.jpg" },
    { name: "Effetto Legno", slug: "effetto-legno", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/p2pg3kqi_tuscania-firenze-3.jpg" },
    { name: "Effetto Marmo", slug: "effetto-marmo", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/embo5gh7_marmo.jpg" },
    { name: "Esterni", slug: "esterni", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/xdhfm2oc_esterni.jpg" },
    { name: "Spessorato 2CM", slug: "spessorato-2cm", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/ef6je1xi_spessorato.jpg" },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-64 bg-navy">
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="text-3xl md:text-4xl font-bold text-white">Pavimenti</h1>
            <p className="text-xl text-white/80 mt-2">Scegli la finitura ideale per il tuo ambiente</p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <Link key={index} to={`/pavimenti/pavimenti-gres/${category.slug}`}>
                <Card className="overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-all duration-300">
                  <div className="aspect-square overflow-hidden bg-gray-200 flex items-center justify-center">
                    {category.img ? (
                      <img src={category.img} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <span className="text-gray-400 text-sm">Foto</span>
                    )}
                  </div>
                  <CardContent className="p-6 bg-gray-50">
                    <h3 className="text-lg font-semibold text-navy text-center">{category.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          {/* Back Link */}
          <div className="mt-12">
            <Link to="/pavimenti" className="inline-flex items-center text-amber-500 hover:text-amber-600 font-medium">
              <ChevronLeft className="w-5 h-5 mr-1" /> Pavimenti e Rivestimenti
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Effetto Pietra Page
const EffettoPietraPage = () => (
  <ProductDetailPage 
    title="Effetto Pietra"
    description="La sezione Effetto Pietra presenta gres porcellanato che riproduce l'aspetto e la materia delle pietre naturali, unendo bellezza senza tempo, texture autentiche e prestazioni tecniche elevate. Queste superfici sono studiate per interni ed esterni, adatte a pavimenti e rivestimenti residenziali e commerciali, e interpretano materiali come ardesia, quarzo, basalto e altre rocce con tonalità e venature che richiamano la natura. Le piastrelle effetto pietra offrono resistenza, facilità di manutenzione e un equilibrio visivo raffinato, ideali per ambienti contemporanei che cercano eleganza e durevolezza unite alla praticità del gres moderno."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/pg489duz_tuscania-colosseoplus-6.jpg"
    features={["Aspetto naturale della pietra", "Alta resistenza all'usura", "Facile manutenzione", "Adatto a interni ed esterni", "Vari formati disponibili"]}
    backLink="/pavimenti/pavimenti-gres"
    backLabel="Pavimenti"
  />
);

// Effetto Cemento Page
const EffettoCementoPage = () => (
  <ProductDetailPage 
    title="Effetto Cemento"
    description="L'effetto cemento riguarda il gres porcellanato che si ispira al calcestruzzo moderno, con superfici materiche, compatte e omogenee che richiamano l'estetica del cemento nell'architettura minimalista e contemporanea. Queste piastrelle possono avere finiture opache o vellutate e si integrano bene in spazi fluidi e minimalisti, enfatizzando la continuità visiva delle superfici. La palette cromatica è neutra e sofisticata (dal grigio cemento a toni tortora, argilla e avorio), mentre le texture variano tra effetto spatolato e crudo compatto per ambienti che risultano accoglienti pur mantenendo un'estetica essenziale. L'uso di piastrelle con fughe minime o quasi assenti amplifica la sensazione di spazio ampio e pulito."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/biohjakb_cemento%20grigio.jpg"
    features={["Stile industriale contemporaneo", "Alta resistenza all'usura", "Facile manutenzione", "Adatto a interni ed esterni", "Vari formati e tonalità"]}
    backLink="/pavimenti/pavimenti-gres"
    backLabel="Pavimenti"
  />
);

// Effetto Legno Page
const EffettoLegnoPage = () => (
  <ProductDetailPage 
    title="Effetto Legno"
    description="La sezione Effetto Legno presenta piastrelle in gres porcellanato che riproducono l'aspetto, le venature e il calore del legno naturale, offrendo un'estetica realistica con la durabilità e la praticità tecnica del gres: resistente all'acqua, all'usura e facile da pulire. I prodotti effetto legno sono studiati per creare atmosfere accoglienti e armoniose, con una vasta gamma di colori neutri e tonalità naturali, dal beige al grigio, dal tortora al miele, con texture che evocano la matericità del legno vivo, adatte a diversi stili d'arredo contemporaneo. Queste piastrelle possono essere utilizzate in molti ambienti della casa — soggiorni, camere, cucine, bagni e anche spazi esterni in versioni antiscivolo — garantendo comfort, continuità visiva e resistenza nel tempo. La versatilità del gres effetto legno permette anche combinazioni creative con altri materiali come pietra, cemento o marmo, e una vasta scelta di formati e pose per personalizzare gli spazi con stile."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/p2pg3kqi_tuscania-firenze-3.jpg"
    features={["Aspetto naturale del legno", "Alta resistenza all'usura", "Resistente all'acqua", "Facile manutenzione", "Vari colori e venature"]}
    backLink="/pavimenti/pavimenti-gres"
    backLabel="Pavimenti"
  />
);

// Effetto Marmo Page
const EffettoMarmoPage = () => (
  <ProductDetailPage 
    title="Effetto Marmo"
    description="La sezione Effetto Marmo è ispirata alla bellezza del marmo naturale, combinate con le prestazioni tecniche superiori del gres: impermeabilità, resistenza a macchie e usura e manutenzione ridotta, ideali per pavimenti e rivestimenti eleganti in vari ambienti. L'effetto marmo riproduce venature e tonalità delle pietre pregiate, offrendo superfici affascinanti con finiture lucide, opache o levigate che esaltano l'estetica e la luce negli spazi moderni e raffinati. Le collezioni includono diverse ispirazioni materiche come: Calacatta, Onice, Travertino, e un'ampia gamma di formati e cromie per adattarsi a progetti residenziali e commerciali di design, consentendo combinazioni con altri materiali e soluzioni creative. Inoltre esistono versioni adatte anche agli esterni, con spessori maggiorati per resistenza alle intemperie, mantenendo continuità visiva tra interno ed esterno."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/embo5gh7_marmo.jpg"
    features={["Eleganza classica del marmo", "Alta resistenza all'usura", "Facile manutenzione", "Superficie lucida o opaca", "Varie venature disponibili"]}
    backLink="/pavimenti/pavimenti-gres"
    backLabel="Pavimenti"
  />
);

// Esterni Page
const EsterniPage = () => (
  <ProductDetailPage 
    title="Esterni"
    description="La sezione Esterno presenta pavimenti in gres pensati per gli spazi outdoor come terrazze, giardini, cortili e camminamenti. Questi materiali si distinguono per elevata resistenza agli agenti atmosferici, all'usura, al gelo e agli sbalzi termici, garantendo così pavimentazioni durevoli, impermeabili e sicure grazie a superfici antiscivolo. Le collezioni disponibili offrono molte soluzioni estetiche e formati (inclusi grandi formati) con effetti che richiamano legno, pietra, marmo, cemento e altri materiali naturali, permettendo di creare ambienti esterni che combinano design e funzionalità senza compromessi. Il gres porcellanato per esterni è ideale per realizzare superfici accoglienti, eleganti e resistenti in varie aree esterne, valorizzando ogni spazio con estetica e performance adatte anche a contesti residenziali e commerciali."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/xdhfm2oc_esterni.jpg"
    features={["Resistente al gelo", "Antiscivolo R11", "Resistente agli agenti atmosferici", "Facile manutenzione", "Vari formati disponibili"]}
    backLink="/pavimenti/pavimenti-gres"
    backLabel="Pavimenti"
  />
);

// Spessorato 2CM Page
const Spessorato2cmPage = () => (
  <ProductDetailPage 
    title="Spessorato 2CM"
    description="La linea Spessorato 2CM sono piastrelle in gres porcellanato da 20 mm di spessore pensata soprattutto per spazi esterni come terrazze, giardini, bordi piscina e camminamenti. Queste superfici sono progettate per offrire prestazioni tecniche elevate: resistenza alla flessione, agli urti, al gelo, alle alte temperature e al fuoco, garantendo durabilità e affidabilità anche in condizioni estreme. È disponibile una larga scelta di modelli in diversi formati e stili, con effetti che riproducono pietra, marmo, cemento, legno e metallo, permettendo di creare continuità estetica tra interno ed esterno e soluzioni progettuali versatili. I materiali sono adatti a vari sistemi di posa e assicurano facilità di installazione, unendo funzionalità e design in un prodotto tecnico e al tempo stesso esteticamente curato."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/ef6je1xi_spessorato.jpg"
    features={["Spessore 2cm per massima resistenza", "Posa su erba, ghiaia o piedini", "Resistente al gelo e agli agenti atmosferici", "Antiscivolo", "Ideale per esterni"]}
    backLink="/pavimenti/pavimenti-gres"
    backLabel="Pavimenti"
  />
);

const SpcPage = () => (
  <ProductDetailPage 
    title="SPC (Stone Plastic Composite)"
    description="I nostri pavimenti SPC sono creati con la tecnologia Real3D. Grazie a queste texture autentiche, al design elegante e ai dettagli realistici, questi pavimenti rendono i tuoi ambienti luminosi e accoglienti. I pavimenti SPC sono la soluzione innovativa per chi cerca un pavimento resistente all'acqua, ultra resistente, facile da installare e dal design moderno, Ideale per ristrutturazioni."
    img="https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/6py3rtvy_spc%20niagara.jpg"
    features={["100% impermeabile", "Installazione flottante click", "Resistente a graffi e urti", "Comfort acustico", "Ideale per bagni e cucine"]}
    backLink="/pavimenti"
    backLabel="Pavimenti e Rivestimenti"
  />
);

const RivestimentiPage = () => {
  const categories = [
    { name: "Gres e Ceramica", slug: "gres-ceramica", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/oy8sbyh3_rivestimento.jpg" },
    { name: "Grandi Formati", slug: "grandi-formati", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/k8y3ljtr_grandi%20formati.jpg" },
    { name: "Pietra Naturale", slug: "pietra-naturale", img: "https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/21oroea2_pietra%20naturale.jpg" },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-64 bg-navy">
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="text-3xl md:text-4xl font-bold text-white">Rivestimenti Ceramici</h1>
            <p className="text-xl text-white/80 mt-2">Eleganza e praticità per ogni ambiente</p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <Link key={index} to={`/pavimenti/rivestimenti-ceramici/${category.slug}`}>
                <Card className="overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-all duration-300">
                  <div className="aspect-square overflow-hidden bg-gray-200 flex items-center justify-center">
                    {category.img ? (
                      <img src={category.img} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <span className="text-gray-400 text-sm">Foto</span>
                    )}
                  </div>
                  <CardContent className="p-6 bg-gray-50">
                    <h3 className="text-lg font-semibold text-navy text-center">{category.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          {/* Back Link */}
          <div className="mt-12">
            <Link to="/pavimenti" className="inline-flex items-center text-amber-500 hover:text-amber-600 font-medium">
              <ChevronLeft className="w-5 h-5 mr-1" /> Pavimenti e Rivestimenti
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Gres e Ceramica Page
const GresCeramicaPage = () => (
  <ProductDetailPage 
    title="Gres e Ceramica"
    description="Puoi scegliere tra diverse proposte che includono superfici con effetti materici come legno, pietra o marmo, capaci di conferire calore, eleganza o lusso contemporaneo agli spazi. Le piastrelle effetto legno replicano la naturalezza del legno senza soffrire l'umidità; quelle effetto pietra creano atmosfere raffinate con tonalità neutre: grigio, beige e sabbia. L'effetto marmo con venature naturali è perfetto per bagni lussuosi. La scelta dei formati e delle finiture aiuta a valorizzare lo spazio: formati più grandi e tonalità chiare ampliano visivamente ambienti piccoli, mentre piastrelle opache o lucide influenzano la percezione della luce. Finiture tridimensionali aggiungono gioco di luci e ombre e impreziosiscono le superfici."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/oy8sbyh3_rivestimento.jpg"
    features={["Ampia gamma di colori e texture", "Facile manutenzione", "Resistente all'umidità", "Vari formati disponibili", "Ideale per bagni e cucine"]}
    backLink="/pavimenti/rivestimenti-ceramici"
    backLabel="Rivestimenti Ceramici"
  />
);

// Grandi Formati Page
const GrandiFormatiPage = () => (
  <ProductDetailPage 
    title="Grandi Formati"
    description="La gamma Grandi Formati propone Lastre fino a 160x320, perfette per rivestimenti. Disponibile in una vasta gamma di effetti e finiture, tra cui marmo, pietra, cemento, metallo e molto altro, è ideale per ambienti residenziali e commerciali di ogni tipo. Con uno spessore di soli 6 mm, sono perfette anche per applicazioni su complementi d'arredo, per progetti total look che uniscono estetica e funzionalità."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/k8y3ljtr_grandi%20formati.jpg"
    features={["Effetto continuo e moderno", "Meno fughe per una superficie più pulita", "Ideale per ambienti ampi", "Varie finiture disponibili", "Alta resistenza"]}
    backLink="/pavimenti/rivestimenti-ceramici"
    backLabel="Rivestimenti Ceramici"
  />
);

// Pietra Naturale Page
const PietraNaturalePage = () => (
  <ProductDetailPage 
    title="Pietra Naturale"
    description="Il fascino intramontabile delle pietre naturali prende vita attraverso una palette cromatica evoluta e raffinata, espressa in pannelli preassemblati con superfici lavorate a mano con la tecnica dello spacco. Gli spessori variano, creando un gioco armonioso di luci e ombre che valorizza ogni singola realizzazione. Questo incontro tra materiali naturali e lavorazioni artigianali di pregio non solo esalta la bellezza autentica della pietra, ma trasforma ogni progetto in un'opera unica, capace di donare profondità e carattere agli spazi. Una scelta che unisce tradizione e innovazione in modo elegante e contemporaneo."
    img="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/21oroea2_pietra%20naturale.jpg"
    features={["Materiale naturale e autentico", "Ogni lastra è unica", "Eleganza senza tempo", "Resistente e durevole", "Ideale per interni ed esterni"]}
    backLink="/pavimenti/rivestimenti-ceramici"
    backLabel="Rivestimenti Ceramici"
  />
);

const FinestrePvcPage = () => (
  <ProductDetailPage 
    title="Finestre in PVC"
    description="Il sistema greenEvolution è un passo avanti nel campo della costruzione di finestre. Finestre in PVC resistenti che si adattano sia ad una facciata che all'interno del locale? Perché no? Grazie alla varietà di tipi di finiture superficiali di telai e ante, utilizzando l'ultima serie di cosiddetti realMaterial, è possibile abbinare serramenti a qualsiasi stile architettonico, dal classico al moderno. Grazie a sei gruppi compatibili di profili greenEvolution, puoi facilmente dare libero sfogo alla tua immaginazione e creare le forme delle finestre più sofisticate. Indipendentemente da quello che desideri il cliente – abbiamo un sistema di profili adatto."
    img="https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/40xulk7x_finestra%20bianca%20bella.jpg"
    features={["Isolamento termico superiore", "Isolamento acustico certificato", "Zero manutenzione", "Resistenza agli agenti atmosferici", "Disponibili in vari colori"]}
    backLink="/serramenti"
    backLabel="Serramenti"
  />
);

const FinestreAlluminioPage = () => (
  <ProductDetailPage 
    title="Finestre in Alluminio"
    description="Le finestre in alluminio per molte persone possono essere associate solo a grandi edifici modernisti, ma il numero di sostenitori delle finestre in alluminio per appartamenti è in costante crescita. Grazie alla moderna tecnologia della loro costruzione, si tratta di finestre in alluminio con isolamento termico, che cambia la loro precedente percezione di sistema freddo. Si inseriscono perfettamente nei criteri del risparmio energetico e degli edifici passivi, grazie ai quali riducono anche i costi di riscaldamento come finestre in alluminio per la casa."
    img="https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ulrw8nnp_aliplast%20alluminio.png"
    features={["Design sottile e moderno", "Alta resistenza strutturale", "Ampia gamma di colori", "Ottimo isolamento termico", "Lunga durata"]}
    backLink="/serramenti"
    backLabel="Serramenti"
  />
);

const AvvolgibiliPage = () => (
  <div className="pt-20">
    <section className="relative h-64 bg-navy">
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <Link to="/serramenti" className="inline-flex items-center text-amber-400 hover:text-amber-300 mb-4">
            <ChevronLeft className="w-5 h-5 mr-1" /> Serramenti
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Avvolgibili</h1>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden p-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ge2xxjl1_avvolgibili.webp" 
              alt="Avvolgibili" 
              className="w-full h-auto object-contain" 
            />
          </div>
          <div>
            <p className="text-lg text-gray-600 mb-4">
              <strong>Nuovo sistema tecnicamente avanzato</strong> per l'installazione sia in edifici esistenti che di nuova costruzione.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Grazie alle soluzioni più recenti, è stato possibile ottenere una tenuta ancora maggiore e ridurre il numero di ponti termici. L'innovativa struttura e la speciale copertura realizzata con i migliori materiali consentono una facile installazione.
            </p>
            <Link to="/contatti">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white">
                Richiedi un Preventivo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-navy mb-6">Caratteristiche</h2>
          <ul className="space-y-3">
            {[
              "Forma del cassonetto: rettangolare",
              "Cassonetto: styropor",
              "Tipo di armatura: alluminio",
              "Profili: alluminio riempito con schiuma poliuretanica",
              "Sportello di ispezione: dal basso verso l'interno",
              "Guide: alluminio, PVC",
              "Ancoraggi di montaggio: sì",
              "Resistenza agli agenti atmosferici: sì",
              "Isolamento acustico (nel cassonetto): sì",
              "Isolamento termico (nel cassonetto): sì",
              "Versioni di Azionamento Disponibili: elettrica, manuale, a molla"
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  </div>
);

const PortoniPage = () => (
  <div className="pt-20">
    <section className="relative h-64 bg-navy">
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <Link to="/serramenti" className="inline-flex items-center text-amber-400 hover:text-amber-300 mb-4">
            <ChevronLeft className="w-5 h-5 mr-1" /> Serramenti
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Portoni Sezionali</h1>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/ze63o48e_sezionale.webp" 
              alt="Portoni Sezionali UniPro" 
              className="w-full h-auto object-contain" 
            />
          </div>
          <div>
            <p className="text-lg text-gray-600 mb-4">
              <strong>Portoni sezionali UniPro</strong> sono la soluzione più confortevole per garage.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              È un portone caldo (il coefficiente di trasmittanza termica Uk = 1,07 [W/m²xK]) destinato specialmente per garage coibentati.
            </p>
            <Link to="/contatti">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white">
                Richiedi un Preventivo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-navy mb-6">Caratteristiche</h2>
          <ul className="space-y-3">
            {[
              "Anta fatta da pannelli d'acciaio 40 mm riempiti della schiuma di poliuretano senza freon",
              "Costruzione di elementi in acciaio zincati",
              "Anta si muove lungo le guide verticali e orizzontali sotto soffitto",
              "Guarnizione di tenuta su tutto il perimetro del portone",
              "Guarnizione aderente al pavimento sul pannello inferiore",
              "Tenuta tra il pannello superiore e l'architrave assicurata dalla guarnizione fissata al primo o al secondo",
              "Pannelli possiedono snodi antipizzicamento e guarnizioni nei posti di giunzione di due pannelli",
              "Disponibile in versione manuale e motorizzata"
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-navy rounded-xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-6">Funzionalità e Sicurezza</h2>
          <ul className="space-y-4">
            {[
              "Pannelli specialmente profilati (dall'interno e dall'esterno) impediscono il pizzicamento delle dita nei posti di giunzione e montaggio delle cerniere e la portina pedonale",
              "Dispositivo paracadute in caso di rottura delle funi di sollevamento",
              "Fotocellule assicurano il bloccaggio del portone e il suo ritorno alla posizione aperta in caso di incontro con un ostacolo",
              "Dispositivo anti sovraccarico nella situazione di incontro con un ostacolo, interruttore montato nel motore blocca l'anta del portone e la fa ritornare su",
              "Dispositivo di sicurezza integrato in caso di rottura della molla a torsione assicura il massimo di sicurezza",
              "Costruzione robusta e il processo unico di formazione di lamiera è la base di benfatto portone sezionale"
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  </div>
);

// Marchi Page
const MarchiPage = () => {
  const brandsPavimenti = [
    { name: "Marazzi", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/7bi6lhza_marazzi.png", url: "https://www.marazzi.it" },
    { name: "Energieker", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/4xh4548i_logo%20energieker.jpg", url: "https://www.energieker.it/it/" },
    { name: "Tuscania", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/jko9derp_tuscania-logo.webp", url: "https://tuscaniagres.it/" },
    { name: "Unicom Starker", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/oliiwt3y_logo-unicom.webp", url: "https://www.unicomstarker.com/" },
    { name: "Mariner", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/49nsm47o_logo-mariner-big.png", url: "https://www.cermariner.it/it/" },
    { name: "Old Sax", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/4g97ymbu_logo%20old-sax.jpg-1.jpeg", url: "https://www.oldsaxceramic.com/" },
    { name: "Virag", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/3cghkoc5_logo%20virag.png", url: "https://www.virag.com/" },
    { name: "Castfloor", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/h0cr242w_logo%20castfloor-laminati.jpg", url: "https://castfloor.com/" },
  ];

  const brandsSerramenti = [
    { name: "Veka", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/8vsqwez8_logo_veka.png", url: null },
    { name: "Salamander", logo: "https://customer-assets.emergentagent.com/job_flooring-win-site/artifacts/q1xs2q4g_logo%20salamander.webp", url: null },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-64 bg-navy">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">I Nostri Marchi</h1>
            <p className="text-xl text-white/80">Collaboriamo con i migliori produttori del settore</p>
          </div>
        </div>
      </section>

      {/* Pavimenti */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-navy mb-8 text-center">Pavimenti e Rivestimenti</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {brandsPavimenti.map((brand, index) => (
              <a 
                key={index} 
                href={brand.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full">
                  <CardContent className="p-8 flex flex-col items-center justify-center min-h-[160px] bg-white">
                    <img 
                      src={brand.logo} 
                      alt={brand.name} 
                      className="max-h-16 max-w-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Serramenti */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-navy mb-8 text-center">Serramenti</h2>
          <div className="flex flex-wrap justify-center gap-6">
            {brandsSerramenti.map((brand, index) => (
              <div key={index} className="w-full sm:w-64">
                <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full">
                  <CardContent className="p-8 flex flex-col items-center justify-center min-h-[160px] bg-white">
                    <img 
                      src={brand.logo} 
                      alt={brand.name} 
                      className="object-contain"
                      style={{ height: brand.name === 'Salamander' ? '80px' : '50px', width: 'auto' }}
                    />
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Chi Siamo Page
const ChiSiamoPage = () => {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-64 bg-navy">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Chi Siamo</h1>
            <p className="text-xl text-white/80">La nostra storia e i nostri valori</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Foto */}
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_casa-redesign/artifacts/814eszmg_ChatGPT%20Image%2026%20gen%202026%2C%2019_08_55.png" 
                alt="Team Spaziocasa+" 
                className="w-full h-auto"
              />
            </div>
            
            {/* Descrizione */}
            <div>
              <p className="text-gray-600 text-lg mb-2 leading-relaxed">
                <strong className="text-navy">Spaziocasa+</strong> nasce da una storia di famiglia, di lavoro concreto e di passione per il costruire.
              </p>
              <p className="text-gray-600 text-lg mb-2 leading-relaxed">
                Nel 1997 nasce <strong>Edil Fa.Ta.</strong>, magazzino edile punto di riferimento per il paese. Anno dopo anno, lavorando fianco a fianco con artigiani pavimentisti il nostro percorso evolve in modo naturale verso il mondo delle finiture.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                È proprio dal contatto diretto con chi posa, misura e vive i materiali che cresce la nostra competenza: prima affianchiamo il reparto finiture, poi scegliamo di dedicarvi sempre più energie, fino a farne il cuore della nostra identità.
              </p>
            </div>
          </div>

          {/* Oggi */}
          <div className="mt-12">
            <h2 className="text-3xl font-bold text-navy mb-4">Oggi</h2>
            <p className="text-gray-600 text-lg mb-2 leading-relaxed max-w-4xl">
              Oggi siamo <strong className="text-navy">Spaziocasa+</strong>, una realtà 100% dedicata alle finiture: pavimenti, rivestimenti e soluzioni pensate per valorizzare ogni spazio, con un approccio che unisce estetica, funzionalità e durata nel tempo.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed max-w-4xl">
              Dal 2026 ampliamo la nostra offerta con una nuova area dedicata ai <strong>serramenti</strong>, un passo naturale che nasce da oltre vent'anni di esperienza nel settore edile e da una visione completa della casa, dall'involucro ai dettagli.
            </p>
          </div>

          {/* Dal progetto alla posa */}
          <div className="mt-12">
            <h2 className="text-3xl font-bold text-navy mb-4">Dal progetto alla posa</h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-4xl">
              Possiamo seguire il cliente dalla progettazione alla posa, coordinando materiali, soluzioni tecniche e installazione, per garantire un risultato finale coerente, curato e senza compromessi.
            </p>
          </div>

          {/* SPC */}
          <div className="mt-12">
            <h2 className="text-3xl font-bold text-navy mb-4">Un'esperienza unica: l'SPC a casa tua</h2>
            <p className="text-gray-600 text-lg mb-2 leading-relaxed max-w-4xl">
              Siamo gli unici a offrire un servizio esclusivo per le pavimentazioni SPC: la dimostrazione direttamente a casa, per vedere, toccare e scegliere il pavimento nel proprio ambiente reale, con luce, spazi e arredi veri.
            </p>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed max-w-4xl italic">
              Un'esperienza concreta, personalizzata e innovativa.<br />
              Proprio come il nostro modo di lavorare.
            </p>
            <Link to="/contatti">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg">
                Contattaci <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contatti Page
const ContattiPage = () => {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-64 bg-navy">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contattaci</h1>
            <p className="text-xl text-white/80">Siamo a tua disposizione per qualsiasi informazione</p>
          </div>
        </div>
      </section>

      {/* Content - Centered Contact Info */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-navy mb-8 text-center">Informazioni di Contatto</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Indirizzo</h3>
                    <p className="text-gray-600">{COMPANY.address}</p>
                    <p className="text-gray-600">{COMPANY.city}</p>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Via+San+Polo+87%2FB+35020+Sant'Angelo+di+Piove+di+Sacco+Padova"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:underline text-sm mt-2 inline-block"
                    >
                      Apri su Google Maps
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Telefono</h3>
                    <a href={`tel:${COMPANY.phone}`} className="text-amber-600 hover:underline text-lg">
                      {COMPANY.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Email</h3>
                    <a href={`mailto:${COMPANY.email}`} className="text-amber-600 hover:underline">
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <HomeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Azienda</h3>
                    <p className="text-gray-600">{COMPANY.companyName}</p>
                    <p className="text-gray-600">P.IVA: {COMPANY.piva}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* CTA Button */}
          <div className="mt-10 text-center">
            <p className="text-gray-600 mb-4">Vuoi richiedere un preventivo? Scrivici!</p>
            <a 
              href={`mailto:${COMPANY.email}?subject=Richiesta%20Preventivo%20-%20Spaziocasa%2B`}
              className="inline-flex items-center px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5 mr-2" />
              Invia Email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <ScrollRestoration />
        <Navigation />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/chi-siamo" element={<ChiSiamoPage />} />
            <Route path="/pavimenti" element={<PavimentiPage />} />
            <Route path="/pavimenti/pavimenti-gres" element={<GresPage />} />
            <Route path="/pavimenti/pavimenti-gres/effetto-pietra" element={<EffettoPietraPage />} />
            <Route path="/pavimenti/pavimenti-gres/effetto-cemento" element={<EffettoCementoPage />} />
            <Route path="/pavimenti/pavimenti-gres/effetto-legno" element={<EffettoLegnoPage />} />
            <Route path="/pavimenti/pavimenti-gres/effetto-marmo" element={<EffettoMarmoPage />} />
            <Route path="/pavimenti/pavimenti-gres/esterni" element={<EsterniPage />} />
            <Route path="/pavimenti/pavimenti-gres/spessorato-2cm" element={<Spessorato2cmPage />} />
            <Route path="/pavimenti/spc" element={<SpcPage />} />
            <Route path="/pavimenti/rivestimenti-ceramici" element={<RivestimentiPage />} />
            <Route path="/pavimenti/rivestimenti-ceramici/gres-ceramica" element={<GresCeramicaPage />} />
            <Route path="/pavimenti/rivestimenti-ceramici/grandi-formati" element={<GrandiFormatiPage />} />
            <Route path="/pavimenti/rivestimenti-ceramici/pietra-naturale" element={<PietraNaturalePage />} />
            <Route path="/pavimenti/arredo-bagno" element={<ArredoBagnoPage />} />
            <Route path="/pavimenti/arredo-bagno/sanitari" element={<SanitariPage />} />
            <Route path="/pavimenti/arredo-bagno/piatti-doccia" element={<PiattiDocciaPage />} />
            <Route path="/pavimenti/arredo-bagno/box-doccia" element={<BoxDocciaPage />} />
            <Route path="/serramenti" element={<SerramentiPage />} />
            <Route path="/serramenti/finestre-pvc" element={<FinestrePvcPage />} />
            <Route path="/serramenti/finestre-alluminio" element={<FinestreAlluminioPage />} />
            <Route path="/serramenti/avvolgibili" element={<AvvolgibiliPage />} />
            <Route path="/serramenti/portoni-sezionali" element={<PortoniPage />} />
            <Route path="/marchi" element={<MarchiPage />} />
            <Route path="/contatti" element={<ContattiPage />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
